// Per-user "recent trades" memory for the TradePicker (U4c).
//
// Mirrors the localStorage recents pattern in InstrumentTypeahead, with one
// deliberate deviation: the storage key is namespaced by a caller-supplied
// identity (the Auth0 subject) so recents do NOT bleed across logins on a
// shared browser. Recents are per-device only (localStorage, not synced) --
// acceptable, and matches the instrument-typeahead tradeoff. All access is
// best-effort: private-mode browsers throw on localStorage, so every helper
// swallows the error and falls back to the empty list rather than crashing the
// picker.

const RECENT_KEY_PREFIX = "recentTrades";
const RECENT_MAX = 8;

function keyFor(namespace: string): string {
  return `${RECENT_KEY_PREFIX}:${namespace}`;
}

export function readRecentTrades(namespace: string): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(keyFor(namespace));
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((v): v is string => typeof v === "string")
      .slice(0, RECENT_MAX);
  } catch {
    return [];
  }
}

function writeRecentTrades(namespace: string, ids: string[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      keyFor(namespace),
      JSON.stringify(ids.slice(0, RECENT_MAX)),
    );
  } catch {
    // ignore
  }
}

// Move `id` to the front (most-recent-first), de-duped, capped at RECENT_MAX,
// and persist. Returns the new list so the caller can update React state in the
// same tick.
export function pushRecentTrade(namespace: string, id: string): string[] {
  const next = [
    id,
    ...readRecentTrades(namespace).filter((existing) => existing !== id),
  ].slice(0, RECENT_MAX);
  writeRecentTrades(namespace, next);
  return next;
}
